<template>
  <div
    ref="draggableScrollbar"
    class="draggable-scroller"
  >
    <slot
      name="arrowLeft"
      :show="showLeftButton"
      :scroll="() => scrollTo(-1)"
    >
      <Arrow
        v-if="(hasArrow || hasArrowDesktop) && showLeftButton"
        aria-hidden="true"
        tabindex="-1"
        class="draggable-scroller__arrow draggable-scroller__arrow--left"
        :class="{ 'draggable-scroller__arrow--desktop': hasArrowDesktop }"
        set-position
        @change="scrollTo(-1)"
      />
    </slot>
    <ul
      v-if="useList"
      ref="element"
      class="draggable-scroller__container"
      :role="containerRole"
      :tabindex="tabindex"
    >
      <slot />
    </ul>
    <div
      v-else
      ref="element"
      class="draggable-scroller__container"
      :role="containerRole"
      :aria-label="containerLabel"
      :tabindex="tabindex"
    >
      <slot />
    </div>
    <slot
      name="arrowRight"
      :show="showRightButton"
      :scroll="() => scrollTo(1)"
    >
      <Arrow
        v-if="(hasArrow || hasArrowDesktop) && showRightButton"
        aria-hidden="true"
        tabindex="-1"
        class="draggable-scroller__arrow draggable-scroller__arrow--right"
        :class="{ 'draggable-scroller__arrow--desktop': hasArrowDesktop }"
        direction="right"
        set-position
        @change="scrollTo(1)"
      />
    </slot>
    <slot
      name="pagination"
      :pages="pages"
      :scroll="(index: number) => scrollToPage(index)"
    >
      <Controls
        v-if="hasPagination && pages.length > 1"
        v-model="isPlaying"
        :pages="pages"
        variant="transparent"
        :has-play-pause="(autoplayTime || 0) > 0"
        do-not-scroll-into-view
        :hide-pages-mobile="hidePaginationMobile"
        :pagination-aria-label="paginationLabel"
        :play-pause-aria-label="playPauseLabel"
        @update="scrollToPage"
      />
    </slot>
  </div>
</template>

<script lang="ts" setup>
import { throttle } from 'lodash-es';
import { Arrow, reducedMotion, Controls } from '@i22team/td-longtail-vue-components';

export type DraggableScrollbarProps = {
  hasArrow?: boolean | undefined;
  hasArrowDesktop?: boolean | undefined;
  hasPagination?: boolean | undefined;
  showSingleActivePagination?: boolean | undefined;
  autoplayTime?: number | undefined;
  autoplayElements?: 'all' | number | undefined;
  containerRole?: string | undefined;
  containerLabel?: string;
  paginationLabel?: string;
  playPauseLabel?: string;
  hidePaginationMobile?: boolean;
  useList?: boolean | undefined;
  tabindex?: number;
};

const props = withDefaults(defineProps<DraggableScrollbarProps>(), {
  hasArrow: false,
  hasArrowDesktop: false,
  hasPagination: false,
  showSingleActivePagination: false,
  autoplayTime: 0,
  autoplayElements: 'all',
  containerRole: undefined,
  containerLabel: undefined,
  paginationLabel: undefined,
  playPauseLabel: undefined,
  useList: false,
  tabindex: undefined,
  hidePaginationMobile: false,
});

const element = useTemplateRef<HTMLDivElement | HTMLUListElement | null>('element');
const draggableScrollbar = useTemplateRef<HTMLDivElement | null>('draggableScrollbar');

const showLeftButton = ref<boolean>(false);
const showRightButton = ref<boolean>(false);
const autoplayTimer = ref<ReturnType<typeof setTimeout> | undefined>(undefined);
const isMouseOver = ref(false);
const isMouseGrabbing = ref(false);
const isPlaying = ref(false);
const isMouseActive = computed(() => isMouseOver.value || isMouseGrabbing.value);

type Page = {
  active: boolean;
  number: number;
};

const pages = ref<Page[]>([]);
const currentPage = ref<number>(0);
const isReducedMotion = reducedMotion.useReducedMotion();

const calculatePages = () => {
  pages.value = [];
  currentPage.value = 0;
  if (!element.value) return;

  const pageWidth = element.value.clientWidth;
  const scrollWidth = element.value.scrollWidth - 1; // for android;
  const numberOfPages = Math.ceil(scrollWidth / pageWidth);
  const delta = 10; // some delta for better snapping.

  const leftBound = Math.floor((element.value.scrollLeft + delta) / pageWidth);

  const rightBound = Math.floor((pageWidth + element.value.scrollLeft - delta) / pageWidth);

  // Calculate the center position of the visible area
  const viewportMiddle = element.value.scrollLeft + pageWidth / 2;

  // The last page may not be complete, so we need to interpolate the width.
  const absoluteMiddleOfLastPage = (scrollWidth - Math.max(0, numberOfPages - 1) * pageWidth) / 2;
  const isLastPageActive =
    Math.max(0, numberOfPages - 2) * pageWidth + absoluteMiddleOfLastPage <
    element.value.scrollLeft;

  for (let i = 0; i < numberOfPages; i += 1) {
    let activePage = i >= leftBound && i <= rightBound;

    if (props.showSingleActivePagination) {
      const pageStart = i * pageWidth;
      const pageEnd = pageStart + pageWidth;

      activePage = viewportMiddle >= pageStart && viewportMiddle < pageEnd;

      // the last page could have a smaller width
      if (i === numberOfPages - 1) {
        activePage = isLastPageActive;
      }
      if (i === numberOfPages - 2 && isLastPageActive) {
        activePage = false;
      }
    }

    pages.value.push({
      number: i,
      active: activePage,
    });
  }
  currentPage.value = Math.floor((element.value.scrollLeft + pageWidth / 2) / pageWidth) + 1;
};

const calculateButtons = () => {
  showLeftButton.value = false;
  showRightButton.value = false;

  if (!element.value) return;
  const scrollWidth = element.value.scrollWidth - 1; // for android;

  if (element.value.clientWidth >= scrollWidth) {
    return;
  }

  if (element.value.scrollLeft > 0) {
    showLeftButton.value = true;
  }

  if (element.value.scrollLeft + element.value.clientWidth < scrollWidth) {
    showRightButton.value = true;
  }
};

const updateScroller = throttle(() => {
  calculatePages();
  calculateButtons();
}, 200);

defineExpose({
  updateScroller,
});

const scrollToPage = (page: number) => {
  if (!element.value) return;
  element.value.scrollTo({
    left: element.value.clientWidth * page,
    behavior: 'smooth',
  });
};

const stopAutoplay = () => {
  if (autoplayTimer.value) {
    clearTimeout(autoplayTimer.value);
  }
};

const getFirstElement = () => {
  const children = element.value?.childNodes;
  if (!children) return undefined;
  return [...children].find(
    (el) => el.nodeName !== '#text' && 'offsetWidth' in el && el.offsetWidth
  );
};

const startAutoplay = () => {
  stopAutoplay(); // Clear any existing timer
  if (!props.autoplayTime && !isPlaying.value) return;
  autoplayTimer.value = setTimeout(() => {
    // Scroll 1 page
    if (props.autoplayElements === 'all') {
      scrollToPage(currentPage.value < pages.value?.length ? currentPage.value + 1 : 0);
    }
    // Scroll n elements
    else {
      // get first element
      const el = getFirstElement() as HTMLElement;

      if (el && element.value) {
        // respect margin
        const scrollOffset = (el.offsetWidth + (el.offsetLeft || 0) * 2) * props.autoplayElements;

        // Scroll amount of elements or 1 display width.
        let scrollTarget =
          element.value.scrollLeft + Math.min(scrollOffset, element.value.offsetWidth);

        // check end and reset
        if (element.value.scrollWidth - element.value.offsetWidth <= scrollTarget) {
          scrollTarget = 0;
        }

        element.value.scrollTo({
          left: scrollTarget,
          behavior: 'smooth',
        });
      }
    }
    startAutoplay(); // Restart timer
  }, props.autoplayTime);
};

// START Drag Scroll on Desktop
const currentPos = ref({ top: 0, left: 0, x: 0, y: 0 });

const removeListener = () => {
  document.removeEventListener('mousemove', mouseMoveHandler);

  document.removeEventListener('mouseup', mouseUpHandler);
};

const mouseMoveHandler = ({ clientX }: PointerEvent | any) => {
  if (!element.value) return;
  const dx = clientX - currentPos.value.x;

  // element.value.scrollTop = currentPos.value.top - dy;
  element.value.scrollLeft = currentPos.value.left - dx;

  // prevent click, when user has moved mouse
  if (Math.abs(dx) > 10) {
    element.value.style.pointerEvents = 'none';
  }
};

watch([isMouseActive, isPlaying], () => {
  stopAutoplay();
  if (!isPlaying.value || isMouseActive.value) return;
  autoplayTimer.value = setTimeout(() => {
    startAutoplay();
  }, 200); // wait for a scroll animation
});

const mouseUpHandler = (e: MouseEvent) => {
  e.preventDefault();
  removeListener();
  isMouseGrabbing.value = false;
  if (!element.value) return;

  element.value.style.cursor = 'grab';
  element.value.style.removeProperty('user-select');
  element.value.style.removeProperty('pointer-events');
};

const mouseDownHandler = (e: MouseEvent | Event) => {
  e.preventDefault();
  removeListener();
  isMouseGrabbing.value = true;

  if (!element.value) return;
  document.addEventListener('mousemove', mouseMoveHandler);
  document.addEventListener('mouseup', mouseUpHandler);

  element.value.style.cursor = 'grabbing';

  if (!('clientX' in e)) return;
  currentPos.value = {
    left: element.value.scrollLeft,
    top: element.value.scrollTop,
    x: e.clientX,
    y: e.clientY,
  };
};
// END Drag Scroll on Desktop
const mouseEnterHandler = () => {
  isMouseOver.value = true;
};

const mouseLeaveHandler = () => {
  isMouseOver.value = false;
};

const startUpTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

onMounted(() => {
  if (!element.value || !draggableScrollbar.value) return;
  draggableScrollbar.value.addEventListener('mouseenter', mouseEnterHandler);
  draggableScrollbar.value.addEventListener('mouseleave', mouseLeaveHandler);
  element.value.addEventListener('mousedown', mouseDownHandler);
  element.value.addEventListener('scroll', updateScroller);
  window.addEventListener('resize', updateScroller);
  updateScroller();

  // on csr we need sometimes a delay.
  startUpTimeout.value = setTimeout(() => {
    updateScroller();
    if (!isReducedMotion.value && props.autoplayTime > 0) {
      isPlaying.value = true;
    }
  }, 100);
});

onBeforeUnmount(() => {
  if (startUpTimeout.value) {
    clearTimeout(startUpTimeout.value);
  }
  if (!element.value || !draggableScrollbar.value) return;
  draggableScrollbar.value.removeEventListener('mouseenter', mouseEnterHandler);
  draggableScrollbar.value.removeEventListener('mouseleave', mouseLeaveHandler);
  element.value.removeEventListener('mousedown', mouseDownHandler);
  element.value.removeEventListener('scroll', updateScroller);
  window.removeEventListener('resize', updateScroller);
  removeListener();
  stopAutoplay();
});

const scrollTo = (direction: number) => {
  if (!element.value) return;
  scrollToPage((currentPage.value as number) + direction - 1);
};
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.draggable-scroller {
  position: relative;

  @include mq(false, $mq-sm - 1) {
    padding-bottom: 4 * $grid-base;
  }

  &__container {
    max-width: 100%;
    overflow-x: scroll;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    list-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__arrow {
    &.arrow {
      opacity: 0;
      transition: opacity 0.25s;
      top: calc(50% - $grid-base * 3 / 2); // pagination
      z-index: 3;
      --stroke-width: 3px;
      font-size: 1.5rem;
    }

    &--desktop {
      @include mq(false, $mq-md - 1) {
        display: none;
      }
    }
  }

  &:hover {
    .draggable-scroller__arrow {
      &.arrow {
        opacity: 0.75;

        &:hover {
          opacity: 1;
        }
      }
    }
  }

  .play-pause-button {
    right: auto;
    left: $grid-base;
  }
}
</style>
